/*
    Multi-selector with all users in a company
*/

import { useQuery } from '@apollo/client';
import { ErrorMessage } from '@hookform/error-message';
import { Control, Controller, FieldPathByValue, FieldValues, useController } from 'react-hook-form';
import { ActionMeta } from 'react-select';
import { z } from 'zod';

import { GetAllCompanyUsersDocument, GetTeamsForCompanyDocument } from '@willow/graphql-iso/src/app';
import { CompanyId, TeamId, UserId } from '@willow/types-iso';

import { getUserName } from '../../../utils';
import { Loader } from '../../Loader/Loader';
import { ReactSelectOverrides, WillowMultiSelect } from './WillowSelect';

export const UserOrTeamSelect = z.array(z.union([z.string().startsWith('user:'), z.string().startsWith('team:')]));

type TeamOrUserResponse = { type: 'user' | 'team'; id: UserId | TeamId };
type Props<T extends FieldValues> = {
  companyId: CompanyId;
  fieldName: FieldPathByValue<T, string[] | undefined>; // Expects prefixed values "user:id" or "team:id"
  control: Control<T>;
  hideErrorMessage?: boolean;
  placeholder?: string;
  isClearable?: boolean;
  onSelectUpdate?: (
    actionMeta: ActionMeta<{
      value: string; // prefixed with "user:" or "team:" for determining type in the reponse
      label: string;
    }>,
    selected: TeamOrUserResponse,
  ) => void;
  reactSelectOverrides?: ReactSelectOverrides;
};

export function ControlledCompanyTeamAndUserMultiSelect<T extends FieldValues>({
  companyId,
  fieldName,
  control,
  hideErrorMessage = false,
  placeholder = 'Select',
  isClearable = false,
  onSelectUpdate,
  reactSelectOverrides,
}: Props<T>) {
  const {
    formState: { errors },
  } = useController({ control, name: fieldName });

  const { data: userData, loading: userLoading } = useQuery(GetAllCompanyUsersDocument, {
    variables: {
      companyId,
    },
  });
  const { data: teamData, loading: teamLoading } = useQuery(GetTeamsForCompanyDocument, {
    variables: {
      companyId,
    },
  });
  if (userLoading || !userData || teamLoading || !teamData) return <Loader />;

  const userOptions = userData.company.allUsers.map((u) => {
    return { value: `user:${u.id}`, label: getUserName(u) };
  });

  const teamOptions = teamData.company.teams.map((t) => {
    return { value: `team:${t.id}`, label: `${t.name}` };
  });

  // Group options by type
  const groupedOptions = [
    ...(teamOptions.length > 0
      ? [
          {
            label: 'Teams',
            options: teamOptions,
          },
        ]
      : []),
    {
      label: 'Members',
      options: userOptions,
    },
  ];

  // Flatten for value filtering
  const allOptions = [...teamOptions, ...userOptions];

  return (
    <>
      <Controller
        name={fieldName}
        control={control}
        render={({ field: { onChange, value } }) => (
          <WillowMultiSelect
            placeholder={placeholder}
            options={groupedOptions}
            value={value ? allOptions.filter((r) => value.includes(r.value)) : []}
            isClearable={isClearable}
            formatSelectedLabel={(option) => {
              // Add "Team" suffix for team selections in the display value
              const isTeam = typeof option.value === 'string' && option.value.startsWith('team:');
              return isTeam ? `${option.label} Team` : option.label;
            }}
            onChange={(users, actionMeta) => {
              if (onSelectUpdate && actionMeta.option) {
                const prefixedValue = actionMeta.option.value;
                const [type, id] = prefixedValue.split(':');
                const selected = {
                  type: type as 'user' | 'team',
                  id: id as UserId | TeamId,
                };
                onSelectUpdate(actionMeta, selected);
              }
              // Store prefixed values to track both users and teams
              onChange(users ? users.map((u) => u.value) : []);
            }}
            {...reactSelectOverrides}
          />
        )}
      />
      {!hideErrorMessage && (
        <ErrorMessage
          errors={errors}
          name={fieldName as any}
          render={({ message }) => <div className="u-fs-2 u-color-red1">{message}</div>}
        />
      )}
    </>
  );
}
